/**
 * Logger utility for consistent logging across environments
 * - error, warn, group, groupEnd: Always log in all environments (critical for production debugging)
 * - log, info, debug, trace: Only log in development and staging (silenced in production/demo)
 * Use this instead of console.* methods in production code
 */
import { ALLOW_DEVELOPMENT, ALLOW_STAGING } from 'utils/config/featureFlags';

export type LogEvent =
  | 'log'
  | 'info'
  | 'debug'
  | 'trace'
  | 'warn'
  | 'error'
  | 'group'
  | 'groupEnd';

/**
 * Returns the appropriate console method based on environment and event type
 * @param event - The type of log event
 * @returns Console method in dev/staging, console method for critical events in prod/demo, or noop for non-critical events in prod/demo
 */
const handleLog = (event: LogEvent) => {
  const eventMap = {
    log: console.log,
    info: console.info,
    debug: console.debug,
    trace: console.trace,
    warn: console.warn,
    error: console.error,
    group: console.group,
    groupEnd: console.groupEnd,
  };

  if (ALLOW_DEVELOPMENT || ALLOW_STAGING) {
    return eventMap[event];
  }

  // Only allow warn, error, group, and groupEnd logs in Production and Demo
  const allowedEvents: LogEvent[] = ['warn', 'error', 'group', 'groupEnd'];
  if (allowedEvents.includes(event)) {
    return eventMap[event];
  }

  // Return no-op for other events in production
  return () => {};
};

/**
 * Environment-aware logger that replaces console.* methods
 * @property {Function} log - Standard log (dev/staging only)
 * @property {Function} info - Info log (dev/staging only)
 * @property {Function} debug - Debug log (dev/staging only)
 * @property {Function} trace - Trace log (dev/staging only)
 * @property {Function} warn - Warning log (all environments)
 * @property {Function} error - Error log (all environments)
 * @property {Function} group - Console group (all environments)
 * @property {Function} groupEnd - Console group end (all environments)
 */
export const Logger = {
  log: handleLog('log'),
  info: handleLog('info'),
  debug: handleLog('debug'),
  trace: handleLog('trace'),
  warn: handleLog('warn'),
  error: handleLog('error'),
  group: handleLog('group'),
  groupEnd: handleLog('groupEnd'),
};
